/**
 * Parts Finder Widget
 *
 * Automatically finds and displays replacement parts for ride-on products
 * by searching for products tagged with parent SKU + "-Parts" suffix.
 *
 * Example: Parent product has tag "URUS-VARI" → searches for products with tag "URUS-VARI-Parts"
 */

(function() {
  'use strict';

  // Ensure ProductWidgets namespace exists
  window.ProductWidgets = window.ProductWidgets || {};
  window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};

  /**
   * PartsFinderWidget Class
   */
  class PartsFinderWidget {
    constructor(orchestrator, container, config) {
      this.orchestrator = orchestrator;
      this.container = container;
      this.lazyLoad = config.lazyLoad || false; // NEW: Add top-level lazyLoad for orchestrator
      this.rendered = false; // NEW: Add rendered flag for orchestrator
      this.config = {
        id: config.id || 'parts-finder',
        lazyLoad: config.lazyLoad || false,
        productHandle: config.productHandle || '',
        productTitle: config.productTitle || '',
        baseSku: config.baseSku || '',
        compatibleSkus: config.compatibleSkus || '', // Comma-separated list of SKUs for reverse mode
        productTags: config.productTags || [],
        maxParts: config.maxParts || 999, // Show all parts by default, search appears when > 8
        isPart: config.isPart || false // Reverse mode: show compatible products for this part
      };

      this.state = {
        loading: false,
        error: null,
        parts: [],
        addingToCart: {}
      };

      // Modal ID
      this.modalId = `parts-finder-modal-${config.id}`;

      // Status colours from CSS variables (dark mode compatible)
      this.colours = this.getStatusColours();
    }

    /**
     * Initialize widget - required by orchestrator
     */
    async init() {
      // Nothing to initialize - widget is ready to render
      return Promise.resolve();
    }

    /**
     * Get status colours from CSS variables (dark mode compatible)
     */
    getStatusColours() {
      const root = document.documentElement;
      const getColor = (varName, fallback) => {
        const value = getComputedStyle(root).getPropertyValue(varName).trim();
        return value ? `rgb(${value})` : fallback;
      };

      return {
        success: getColor('--color-status-success', 'rgb(34, 197, 94)'),
        error: getColor('--color-status-error', 'rgb(239, 68, 68)'),
        warning: getColor('--color-status-warning', 'rgb(255, 193, 7)'),
        info: getColor('--color-status-info', 'rgb(59, 130, 246)'),
        grey: getColor('--gray-400', 'rgb(156, 163, 175)'),
        greyText: getColor('--gray-600', 'rgb(107, 114, 128)'),
        spinnerBg: '#f3f3f3', // Light spinner background
        spinnerColor: '#3498db', // Blue spinner
        buttonPrimary: '#2563eb', // Blue button
        buttonSuccess: '#10b981', // Green success
        buttonError: '#dc2626', // Red error
        errorBg: '#fee', // Light pink error bg
        errorBorder: '#fcc', // Light pink error border
        errorText: '#c33' // Dark red error text
      };
    }

    /**
     * Hide the widget when there is nothing to show. The container is itself the
     * .pw-widget in the widget grid, whose desktop rules force display: flex with
     * !important, so an inline display: none is overridden and whatever was drawn
     * (the loading spinner) stays on screen. The hidden attribute plus the
     * pw-widget--empty class are what the grid CSS honours, and the markup is
     * cleared so nothing can linger even if a rule slips through.
     */
    hideEmpty() {
      const targets = [this.container];
      if (this.container.parentElement && this.container.parentElement.classList.contains('pw-widget')) {
        targets.push(this.container.parentElement);
      }
      this.container.innerHTML = '';
      targets.forEach(el => {
        el.hidden = true;
        el.classList.add('pw-widget--empty');
        el.classList.remove('pw-skeleton-widget');
        el.style.display = 'none';
      });
    }

    /**
     * Render the widget
     */
    async render() {
      if (!this.config.baseSku) {
        this.hideEmpty();
        return;
      }

      this.state.loading = true;
      this.showLoading();

      try {
        // Fetch parts for this product
        const parts = await this.fetchParts();

        if (!parts || parts.length === 0) {
          this.hideEmpty();
          return;
        }

        this.state.parts = parts;

        // Build widget badge
        this.container.innerHTML = this.buildWidgetHTML();

        // Build and append modal
        this.buildModal();

        // Attach event listeners
        this.attachEventListeners();

        // Mark as rendered
        this.rendered = true;

        // Add loaded class for skeleton transition
        if (this.container) {
          this.container.classList.add('pw-widget--loaded');
          this.container.classList.remove('pw-skeleton-widget');
        }
      } catch (error) {
        console.error('[PartsFinderWidget] Error rendering:', error);
        this.state.error = error.message;
        this.renderError();
      } finally {
        this.state.loading = false;
      }
    }

    /**
     * Fetch parts products by searching for base SKU + "-Parts" tag
     * OR if in reverse mode (isPart=true), fetch compatible products with base SKU tags
     */
    async fetchParts() {
      if (this.config.isPart) {
        // Reverse mode: Find products tagged with ANY of the compatible SKUs
        // Since there can be many SKUs (e.g., 95), we use the fallback method
        // which fetches all products and filters in JavaScript
        const compatibleSkusArray = this.config.compatibleSkus
          ? this.config.compatibleSkus.split(',').map(s => s.trim())
          : [this.config.baseSku];

        return await this.fetchCompatibleProducts(compatibleSkusArray);
      } else {
        // Normal mode: Find parts tagged with base SKU + "-Parts"
        const searchTag = `${this.config.baseSku}-Parts`;
        const searchUrl = `/search?q=tag:${encodeURIComponent(searchTag)}&view=json&limit=50`;

        try {
          const response = await fetch(searchUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (!response.ok) {
            return await this.fetchPartsFallback(searchTag);
          }

          const data = await response.json();

          return this.parsePartsFromSearch(data);
        } catch (error) {
          console.error('[PartsFinderWidget] Error fetching:', error);
          return await this.fetchPartsFallback(searchTag);
        }
      }
    }

    /**
     * Fetch compatible products for a part (reverse mode)
     * Uses search API to efficiently find products by SKU tags
     * Much faster than paginating through all products
     */
    async fetchCompatibleProducts(compatibleSkus) {
      try {
        const allResults = [];
        const batchSize = 10; // Process 10 SKUs at a time for parallel requests
        const batches = [];

        // Split SKUs into batches
        for (let i = 0; i < compatibleSkus.length; i += batchSize) {
          batches.push(compatibleSkus.slice(i, i + batchSize));
        }

        // Process batches in parallel
        for (const batch of batches) {
          const batchPromises = batch.map(sku =>
            this.searchProductsBySku(sku).catch(err => {
              console.error('[PartsFinderWidget] Error searching for SKU', sku, ':', err);
              return [];
            })
          );

          const batchResults = await Promise.all(batchPromises);

          // Flatten and deduplicate results
          batchResults.forEach(results => {
            results.forEach(product => {
              // Deduplicate by ID
              if (!allResults.find(p => p.id === product.id)) {
                allResults.push(product);
              }
            });
          });
        }

        // Filter out parts products and ensure we only have main parent products
        const compatibleProducts = allResults.filter(product => {
          // Skip if it's a parts product
          if (product.product_type === 'Parts' || product.product_type === 'parts') {
            return false;
          }

          // Check tags for additional filtering
          if (product.tags) {
            const tags = Array.isArray(product.tags) ? product.tags : [];
            const tagsLower = tags.map(t => (typeof t === 'string' ? t.toLowerCase() : ''));

            // Exclude if tagged as a part
            if (tagsLower.includes('product type_parts')) {
              return false;
            }

            // Only include main parent products
            if (!tagsLower.includes('main parent_yes')) {
              return false;
            }
          }

          return true;
        });

        return compatibleProducts.slice(0, this.config.maxParts);
      } catch (error) {
        console.error('[PartsFinderWidget] Error fetching compatible products:', error);
        return [];
      }
    }

    /**
     * Search for products by a single SKU tag using search API
     * Filters to only return main parent products, not parts
     */
    async searchProductsBySku(sku) {
      try {
        const searchUrl = `/search?q=tag:${encodeURIComponent(sku)}&view=json&limit=50`;
        const response = await fetch(searchUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          return [];
        }

        const data = await response.json();

        // Parse and filter results
        const products = (data.results || [])
          .filter(item => {
            // Filter out parts products
            if (item.product_type === 'Parts' || item.product_type === 'parts') {
              return false;
            }

            // Check tags if available
            if (item.tags) {
              const tags = Array.isArray(item.tags) ? item.tags : [];
              const tagsLower = tags.map(t => (typeof t === 'string' ? t.toLowerCase() : ''));

              // Exclude if tagged as a part
              if (tagsLower.includes('product type_parts')) {
                return false;
              }

              // Only include if tagged as main parent
              if (!tagsLower.includes('main parent_yes')) {
                return false;
              }
            } else {
              // If no tags, exclude it (can't verify it's a main parent)
              return false;
            }

            return true;
          })
          .map(item => ({
            id: item.id,
            handle: item.handle,
            title: item.title,
            price: item.price,
            compareAtPrice: item.compare_at_price,
            available: item.available,
            variantId: item.variants?.[0]?.id || item.id,
            image: item.images?.[0] || item.featured_image || null,
            url: item.url || `/products/${item.handle}`,
            product_type: item.product_type,
            tags: item.tags
          }));

        return products;
      } catch (error) {
        console.error('[PartsFinderWidget] Error in searchProductsBySku:', error);
        return [];
      }
    }

    /**
     * Fallback method to fetch parts using collections API
     */
    async fetchPartsFallback(searchTag) {
      try {
        // Try to fetch from a "parts" collection or all products
        const url = `/collections/all/products.json?limit=250`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();

        // Filter products that have the search tag
        const parts = data.products.filter(product => {
          return product.tags && product.tags.some(tag =>
            tag.toLowerCase() === searchTag.toLowerCase()
          );
        });

        return parts.slice(0, this.config.maxParts).map(product => ({
          id: product.id,
          handle: product.handle,
          title: product.title,
          price: product.variants[0].price,
          compareAtPrice: product.variants[0].compare_at_price,
          available: product.variants[0].available,
          variantId: product.variants[0].id,
          image: product.images[0] || null,
          url: `/products/${product.handle}`
        }));
      } catch (error) {
        console.error('[PartsFinderWidget] Fallback method failed:', error);
        return [];
      }
    }

    /**
     * Parse parts data from search results
     */
    parsePartsFromSearch(data) {
      // Handle different possible response formats
      const products = data.products || data.results || [];

      return products.slice(0, this.config.maxParts).map(product => ({
        id: product.id,
        handle: product.handle,
        title: product.title,
        price: product.variants?.[0]?.price || product.price,
        compareAtPrice: product.variants?.[0]?.compare_at_price,
        available: product.variants?.[0]?.available ?? product.available,
        variantId: product.variants?.[0]?.id || product.variant_id,
        image: product.images?.[0] || product.image || product.featured_image,
        url: product.url || `/products/${product.handle}`
      }));
    }

    /**
     * Show loading state
     */
    showLoading() {
      const loadingText = this.config.isPart
        ? 'Checking compatible products...'
        : 'Checking parts availability...';

      this.container.innerHTML = `
        <div style="padding: 20px; text-align: center;">
          <div class="pw-spinner" style="
            display: inline-block;
            width: 40px;
            height: 40px;
            border: 4px solid ${this.colours.spinnerBg};
            border-top: 4px solid ${this.colours.spinnerColor};
            border-radius: 50%;
            animation: pw-spin 1s linear infinite;
          "></div>
          <p style="margin-top: 12px; color: ${this.colours.greyText};">${loadingText}</p>
        </div>
      `;
    }

    /**
     * Build widget HTML - Persuasive badge with psychological triggers
     */
    buildWidgetHTML() {
      const partsCount = this.state.parts.length;
      const isReverse = this.config.isPart;

      // Dynamic text based on mode
      const headingText = isReverse ? 'Compatible Products Available' : 'Genuine Parts, Always Available';
      const descText = isReverse
        ? `<strong style="color: rgb(var(--color-base-button));">${partsCount} compatible product${partsCount !== 1 ? 's' : ''}</strong> work with this part. See which ride-ons you can use this with.`
        : `<strong style="color: rgb(var(--color-base-button));">${partsCount} replacement part${partsCount !== 1 ? 's' : ''}</strong> ready to ship. Don't get stuck without parts like with other retailers.`;
      const buttonText = isReverse ? `View ${partsCount} Compatible Product${partsCount !== 1 ? 's' : ''}` : `View ${partsCount} Part${partsCount !== 1 ? 's' : ''}`;
      const ariaLabel = isReverse ? `View ${partsCount} compatible products for this part` : `View ${partsCount} available replacement parts`;

      return `
        <div style="background: linear-gradient(135deg, rgb(var(--color-base-button) / 0.08), rgb(var(--color-base-button) / 0.03)); border: 2px solid rgb(var(--color-base-button) / 0.2); border-radius: var(--rounded-card); padding: var(--sp-5);">

          <!-- Header -->
          <div style="text-align: center; margin-bottom: var(--sp-4);">

            <!-- Badge -->
            <div style="display: inline-flex; align-items: center; gap: 0.375rem; background: rgb(var(--color-base-button)); color: white; padding: 0.25rem 0.75rem; border-radius: 999px; font-size: 0.6875rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 1rem;">
              <svg style="width: 0.875rem; height: 0.875rem;" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              Industry First
            </div>

            <h3 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-lg); line-height: 1.2; color: rgb(var(--color-drawer-text)); margin: 0 0 var(--sp-2) 0;">
              ${headingText}
            </h3>
            <p style="font-size: var(--text-sm); line-height: 1.5; color: rgb(var(--color-drawer-text) / 0.75); margin: 0 0 var(--sp-3) 0;">
              ${descText}
            </p>

            <!-- Trust Signals -->
            <div style="display: flex; align-items: center; justify-content: center; gap: 1rem; flex-wrap: wrap; font-size: 0.6875rem; color: rgb(var(--color-drawer-text) / 0.65); margin-bottom: var(--sp-4);">
              <div style="display: flex; align-items: center; gap: 0.25rem;">
                <svg style="width: 0.875rem; height: 0.875rem; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                <span>Compatibility verified</span>
              </div>
              <div style="display: flex; align-items: center; gap: 0.25rem;">
                <svg style="width: 0.875rem; height: 0.875rem; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                <span>Full stock maintained</span>
              </div>
              <div style="display: flex; align-items: center; gap: 0.25rem;">
                <svg style="width: 0.875rem; height: 0.875rem; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                <span>Fast despatch</span>
              </div>
            </div>
          </div>

          <!-- View Parts Button -->
          <div style="text-align: center;">
            <button
              type="button"
              class="button button--secondary"
              is="hover-button"
              aria-controls="${this.modalId}"
              aria-expanded="false"
              aria-label="${ariaLabel}"
              style="width: 100%; max-width: 300px;"
            >
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">
                ${buttonText}
              </span>
            </button>
          </div>

        </div>
      `;
    }

    /**
     * Build modal using theme's drawer system with persuasive content
     */
    buildModal() {
      const partsCount = this.state.parts.length;
      const showSearch = partsCount > 8;
      const isReverse = this.config.isPart;

      // Dynamic modal text
      const modalTitle = isReverse ? 'Compatible Products' : 'Replacement Parts';
      const modalAriaLabel = isReverse ? 'Compatible Products' : 'Replacement Parts';
      const heroTitle = isReverse
        ? `${partsCount} Compatible Products for ${this.escapeHtml(this.config.productTitle)}`
        : `${partsCount} Genuine Parts for ${this.escapeHtml(this.config.productTitle)}`;
      const heroDesc = isReverse
        ? '<strong>Industry-first compatibility tracking:</strong> We maintain a comprehensive parts matrix so you know exactly which products work with this part. Most retailers don\'t offer this level of transparency.'
        : '<strong>Industry-first parts availability:</strong> We maintain a comprehensive compatibility matrix and full stock of replacement parts. Most retailers don\'t offer this level of support.';

      const modalHTML = `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="${modalAriaLabel}" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${modalTitle}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section with Persuasive Messaging -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-base-button)); border-radius: 50%; margin-bottom: 1rem;">
                    <svg style="width: 2rem; height: 2rem; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5"></path>
                    </svg>
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${heroTitle}
                  </h3>
                  <p class="text-base" style="max-width: 600px; margin: 0 auto 1rem auto; color: rgb(var(--color-drawer-text) / 0.85);">
                    ${heroDesc}
                  </p>

                  <!-- Trust Badges -->
                  <div style="display: flex; align-items: center; justify-content: center; gap: 1.25rem; flex-wrap: wrap; margin-bottom: 1.5rem;">
                    <div style="text-align: center;">
                      <div style="font-size: 1.5rem; font-weight: 700; color: rgb(var(--color-base-button));">${partsCount}</div>
                      <div style="font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.65);">${isReverse ? 'Compatible Products' : 'Parts in Stock'}</div>
                    </div>
                    <div style="width: 1px; height: 2rem; background: rgb(var(--color-drawer-text) / 0.15);"></div>
                    <div style="text-align: center;">
                      <div style="font-size: 1.5rem; font-weight: 700; color: rgb(var(--color-base-button));">100%</div>
                      <div style="font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.65);">Compatibility</div>
                    </div>
                    <div style="width: 1px; height: 2rem; background: rgb(var(--color-drawer-text) / 0.15);"></div>
                    <div style="text-align: center;">
                      <div style="font-size: 1.5rem; font-weight: 700; color: rgb(var(--color-base-button));">24h</div>
                      <div style="font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.65);">Despatch</div>
                    </div>
                  </div>
                </div>

                ${showSearch ? this.buildSearchBox() : ''}

                <!-- Parts Grid -->
                <div id="${this.modalId}-parts-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem;">
                  ${this.state.parts.map(part => this.renderPartCard(part)).join('')}
                </div>

                <!-- Competitor Comparison Section (only for parts mode) -->
                ${!isReverse ? this.buildCompetitorComparisonSection() : ''}

                <!-- Key Insights (only for parts mode) -->
                ${!isReverse ? this.buildKeyInsightsSection() : ''}

                <!-- Bottom Trust Message -->
                <div style="padding: 1.25rem; background: rgb(var(--color-base-button) / 0.08); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.2); text-align: center;">
                  <p style="font-size: 0.875rem; margin: 0 0 0.75rem 0; color: rgb(var(--color-drawer-text)); font-weight: 500; line-height: 1.6;">
                    <strong>Why RiiRoo is different:</strong> ${isReverse ? 'We track which products work with every part. Know exactly what you\'re buying—unlike most retailers who don\'t provide this information.' : 'We maintain full parts stock and compatibility data. When something breaks, we actually have the parts to fix it quickly—unlike most competitors.'}
                  </p>
                  <div style="display: flex; align-items: center; justify-content: center; gap: 1rem; flex-wrap: wrap; font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.7);">
                    ${isReverse ? '<span>✓ Full compatibility tracking</span><span>✓ Transparent info</span><span>✓ Real support</span>' : '<span>✓ Parts matrix maintained</span><span>✓ Fast resolution</span><span>✓ Real support</span>'}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;

      // Append to body
      const modalElement = document.createElement('div');
      modalElement.innerHTML = modalHTML;
      document.body.appendChild(modalElement.firstElementChild);
    }

    /**
     * Build search box for filtering parts (when > 8 parts)
     */
    buildSearchBox() {
      return `
        <div style="margin-bottom: 1rem;">
          <div style="position: relative; max-width: 500px; margin: 0 auto;">
            <input
              type="text"
              id="${this.modalId}-search"
              placeholder="Search parts by name..."
              style="
                width: 100%;
                padding: 0.75rem 1rem 0.75rem 2.75rem;
                border: 2px solid rgb(var(--color-drawer-text) / 0.15);
                border-radius: var(--rounded-button);
                font-size: 0.9375rem;
                background: rgb(var(--color-drawer-background));
                color: rgb(var(--color-drawer-text));
                transition: all 0.2s ease;
              "
              onfocus="this.style.borderColor='rgb(var(--color-base-button))'; this.style.boxShadow='0 0 0 3px rgb(var(--color-base-button) / 0.1)'"
              onblur="this.style.borderColor='rgb(var(--color-drawer-text) / 0.15)'; this.style.boxShadow='none'"
            />
            <svg style="position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); width: 1.25rem; height: 1.25rem; color: rgb(var(--color-drawer-text) / 0.4); pointer-events: none;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <div id="${this.modalId}-search-count" style="text-align: center; margin-top: 0.5rem; font-size: 0.8125rem; color: rgb(var(--color-drawer-text) / 0.65);"></div>
        </div>
      `;
    }

    /**
     * Build competitor comparison section
     */
    buildCompetitorComparisonSection() {
      return `
        <div style="padding: 1.25rem; background: linear-gradient(135deg, rgb(var(--color-base-button) / 0.05), rgb(var(--color-base-button) / 0.02)); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.15);">

          <!-- Section Header -->
          <div style="text-align: center; margin-bottom: 1.5rem;">
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; background: rgb(var(--color-base-button)); border-radius: 50%; margin-bottom: 0.75rem;">
              <svg style="width: 1.25rem; height: 1.25rem; color: white;" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
            <h3 class="heading font-bold" style="font-size: 1.125rem; margin-bottom: 0.5rem; color: rgb(var(--color-drawer-text));">
              How RiiRoo Compares to Competitors
            </h3>
            <p style="font-size: 0.8125rem; max-width: 600px; margin: 0 auto; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.4;">
              We researched major retailers. Here's the truth about parts availability.
            </p>
          </div>

          <!-- Comparison Cards (Mobile First) -->
          <div style="display: grid; grid-template-columns: 1fr; gap: 1rem; margin-bottom: 1.5rem;">

            <!-- Parts Availability -->
            <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <div style="font-size: 0.875rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 0.75rem;">Parts Availability</div>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.625rem; font-size: 0.75rem;">

                <div style="padding: 0.625rem; background: rgb(var(--color-base-button) / 0.08); border-radius: 6px; border: 1px solid rgb(var(--color-base-button) / 0.2);">
                  <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-base-button)); text-transform: uppercase; margin-bottom: 4px;">RiiRoo</div>
                  <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text)); font-weight: 600;">
                    <svg style="width: 0.875rem; height: 0.875rem; color: ${this.colours.success};" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                    <span>Full stock</span>
                  </div>
                </div>

                <div style="padding: 0.625rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: 6px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; margin-bottom: 4px;">OutdoorToys</div>
                  <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text) / 0.85); font-weight: 500;">
                    <svg style="width: 0.875rem; height: 0.875rem; color: ${this.colours.error};" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                    <span>Not available</span>
                  </div>
                </div>

                <div style="padding: 0.625rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: 6px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; margin-bottom: 4px;">EpicPlay</div>
                  <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text) / 0.85); font-weight: 500;">
                    <svg style="width: 0.875rem; height: 0.875rem; color: ${this.colours.error};" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                    <span>No parts</span>
                  </div>
                </div>

                <div style="padding: 0.625rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: 6px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; margin-bottom: 4px;">FunBikes</div>
                  <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text) / 0.85); font-weight: 500;">
                    <svg style="width: 0.875rem; height: 0.875rem; color: ${this.colours.grey};" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
                    </svg>
                    <span>Unknown</span>
                  </div>
                </div>

              </div>
            </div>

            <!-- Compatibility Matrix -->
            <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <div style="font-size: 0.875rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 0.75rem;">Compatibility Matrix</div>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.625rem; font-size: 0.75rem;">

                <div style="padding: 0.625rem; background: rgb(var(--color-base-button) / 0.08); border-radius: 6px; border: 1px solid rgb(var(--color-base-button) / 0.2);">
                  <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-base-button)); text-transform: uppercase; margin-bottom: 4px;">RiiRoo</div>
                  <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text)); font-weight: 600;">
                    <svg style="width: 0.875rem; height: 0.875rem; color: ${this.colours.success};" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                    <span>Maintained</span>
                  </div>
                </div>

                <div style="padding: 0.625rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: 6px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; margin-bottom: 4px;">Others</div>
                  <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text) / 0.85); font-weight: 500;">
                    <svg style="width: 0.875rem; height: 0.875rem; color: ${this.colours.error};" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                    <span>Not maintained</span>
                  </div>
                </div>

              </div>
            </div>

          </div>

        </div>
      `;
    }

    /**
     * Build key insights section
     */
    buildKeyInsightsSection() {
      return `
        <div style="display: flex; flex-direction: column; gap: 0.875rem;">

          <!-- RiiRoo Advantage -->
          <div style="padding: 0.875rem; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.3);">
            <div style="display: flex; align-items: start; gap: 0.625rem;">
              <div style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button)); border-radius: 50%;">
                <svg style="width: 1rem; height: 1rem; color: white;" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div style="flex: 1;">
                <h4 class="font-bold" style="font-size: 0.8125rem; margin-bottom: 0.375rem; color: rgb(var(--color-drawer-text)); display: flex; align-items: center; gap: 4px;">
                  RiiRoo's Industry-First Approach
                </h4>
                <p style="font-size: 0.75rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.4;">
                  <strong>Full parts stock with compatibility tracking.</strong> We're the only retailer maintaining a comprehensive parts matrix. When something breaks, we actually have the parts to fix it.
                </p>
              </div>
            </div>
          </div>

          <!-- Competitor Warning -->
          <div style="padding: 0.875rem; background: ${this.colours.warning.replace('rgb(', 'rgba(').replace(')', ', 0.1)')}; border-radius: var(--rounded-card); border: 2px solid ${this.colours.warning.replace('rgb(', 'rgba(').replace(')', ', 0.3)')};">
            <div style="display: flex; align-items: start; gap: 0.625rem;">
              <div style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: ${this.colours.warning}; border-radius: 50%;">
                <svg style="width: 1rem; height: 1rem; color: white;" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div style="flex: 1;">
                <h4 class="font-bold" style="font-size: 0.8125rem; margin-bottom: 0.375rem; color: rgb(var(--color-drawer-text));">
                  The Industry Problem
                </h4>
                <p style="font-size: 0.75rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.4;">
                  <strong>Most retailers don't stock replacement parts.</strong> They'll happily sell you a ride-on, but when it breaks? You're on your own. No parts, no support, no solution.
                </p>
              </div>
            </div>
          </div>

        </div>
      `;
    }

    /**
     * Old renderContent method - now replaced by buildWidgetHTML + buildModal
     */
    OLD_renderContent() {
      const partsHtml = this.state.parts.map(part => this.renderPartCard(part)).join('');

      this.container.innerHTML = `
        <div class="pw-parts-finder-widget" style="
          background: rgb(var(--pw-surface-primary));
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin: 24px 0;
        ">
          <!-- Header -->
          <div style="margin-bottom: 20px;">
            <h3 style="
              font-size: 20px;
              font-weight: 700;
              color: #1a1a1a;
              margin: 0 0 8px 0;
              display: flex;
              align-items: center;
              gap: 8px;
            ">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
              Replacement Parts Available
            </h3>
            <p style="
              font-size: 14px;
              color: ${this.colours.greyText};
              margin: 0;
            ">
              Compatible with ${this.escapeHtml(this.config.productTitle)}
            </p>
          </div>

          <!-- Parts Grid -->
          <div class="pw-parts-grid" style="
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 16px;
          ">
            ${partsHtml}
          </div>
        </div>
      `;

      // Attach event listeners
      this.attachEventListeners();
    }

    /**
     * Render individual part card - matching theme design
     */
    renderPartCard(part) {
      const isAdding = this.state.addingToCart[part.variantId];
      const formattedPrice = this.formatMoney(part.price);
      const hasDiscount = part.compareAtPrice && part.compareAtPrice > part.price;
      const formattedComparePrice = hasDiscount ? this.formatMoney(part.compareAtPrice) : null;

      return `
        <div
          data-part-title="${this.escapeHtml(part.title)}"
          style="
            border: 1px solid rgb(var(--color-drawer-text) / 0.12);
            border-radius: var(--rounded-card);
            overflow: hidden;
            background: rgb(var(--color-drawer-background));
            display: flex;
            flex-direction: column;
          ">
          <!-- Image -->
          <a href="${part.url}" style="
            display: block;
            position: relative;
            padding-top: 100%;
            background: rgb(var(--color-drawer-text) / 0.03);
            overflow: hidden;
          ">
            ${part.image ? `
              <img
                src="${this.getImageUrl(part.image, '400x400')}"
                alt="${this.escapeHtml(part.title)}"
                style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                "
              />
            ` : `
              <div style="
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: rgb(var(--color-drawer-text) / 0.3);
              ">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
            `}
          </a>

          <!-- Content -->
          <div style="padding: var(--sp-3); flex: 1; display: flex; flex-direction: column;">
            <a href="${part.url}" style="
              display: block;
              font-size: var(--text-sm);
              font-weight: 600;
              color: rgb(var(--color-drawer-text));
              text-decoration: none;
              margin-bottom: var(--sp-2);
              line-height: 1.4;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            ">
              ${this.escapeHtml(part.title)}
            </a>

            <!-- Price -->
            <div style="margin-bottom: var(--sp-3);">
              ${hasDiscount ? `
                <div style="display: flex; align-items: center; gap: var(--sp-2);">
                  <span style="
                    font-size: var(--text-base);
                    font-weight: 700;
                    color: rgb(var(--color-drawer-text));
                  ">${formattedPrice}</span>
                  <span style="
                    font-size: var(--text-xs);
                    color: rgb(var(--color-drawer-text) / 0.5);
                    text-decoration: line-through;
                  ">${formattedComparePrice}</span>
                </div>
              ` : `
                <span style="
                  font-size: var(--text-base);
                  font-weight: 700;
                  color: rgb(var(--color-drawer-text));
                ">${formattedPrice}</span>
              `}
            </div>

            <!-- Add to Cart Button -->
            <button
              class="button button--primary pw-add-to-cart-btn"
              is="hover-button"
              data-variant-id="${part.variantId}"
              ${!part.available || isAdding ? 'disabled' : ''}
              aria-label="Add ${this.escapeHtml(part.title)} to cart"
              style="width: 100%; margin-top: auto;"
            >
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">
                ${isAdding ? 'Adding...' : part.available ? 'Add to Cart' : 'Out of Stock'}
              </span>
            </button>
          </div>
        </div>
      `;
    }

    /**
     * Attach event listeners
     * Note: Modal open/close is handled automatically by theme's x-modal system via aria-controls
     */
    attachEventListeners() {
      // Add to cart buttons (inside modal)
      const modal = document.getElementById(this.modalId);
      if (modal) {
        const addToCartButtons = modal.querySelectorAll('.pw-add-to-cart-btn');
        addToCartButtons.forEach(button => {
          button.addEventListener('click', async (e) => {
            const variantId = button.getAttribute('data-variant-id');
            await this.addToCart(variantId, button);
          });
        });

        // Search functionality (if search box exists)
        const searchInput = document.getElementById(`${this.modalId}-search`);
        if (searchInput) {
          searchInput.addEventListener('input', (e) => {
            this.filterParts(e.target.value);
          });
        }
      }
    }

    /**
     * Filter parts based on search query
     */
    filterParts(query) {
      const partsGrid = document.getElementById(`${this.modalId}-parts-grid`);
      const searchCount = document.getElementById(`${this.modalId}-search-count`);

      if (!partsGrid) return;

      const searchLower = query.toLowerCase().trim();
      let visibleCount = 0;

      // Get all part cards
      const partCards = partsGrid.querySelectorAll('[data-part-title]');

      partCards.forEach(card => {
        const partTitle = card.getAttribute('data-part-title').toLowerCase();

        if (searchLower === '' || partTitle.includes(searchLower)) {
          card.style.display = '';
          visibleCount++;
        } else {
          card.style.display = 'none';
        }
      });

      // Update count display
      if (searchCount) {
        if (searchLower === '') {
          searchCount.textContent = '';
        } else {
          searchCount.textContent = `Showing ${visibleCount} of ${this.state.parts.length} parts`;
          if (visibleCount === 0) {
            searchCount.innerHTML = `<span style="color: ${this.colours.error};">No parts found matching "${this.escapeHtml(query)}"</span>`;
          }
        }
      }
    }

    /**
     * Add part to cart
     */
    async addToCart(variantId, button) {
      if (this.state.addingToCart[variantId]) return;

      this.state.addingToCart[variantId] = true;
      button.disabled = true;
      button.innerHTML = `
        <span style="
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: pw-spin 0.6s linear infinite;
        "></span>
        Adding...
      `;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Success animation
        button.style.background = this.colours.buttonSuccess;
        button.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Added!
        `;

        // Trigger cart drawer refresh if it exists
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }

        // Dispatch custom event for cart update
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Reset button after 2 seconds
        setTimeout(() => {
          button.style.background = this.colours.buttonPrimary;
          button.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Add to Cart
          `;
          button.disabled = false;
          this.state.addingToCart[variantId] = false;
        }, 2000);

      } catch (error) {
        console.error('[PartsFinderWidget] Error adding to cart:', error);

        button.style.background = this.colours.buttonError;
        button.innerHTML = 'Error - Try Again';

        setTimeout(() => {
          button.style.background = this.colours.buttonPrimary;
          button.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Add to Cart
          `;
          button.disabled = false;
          this.state.addingToCart[variantId] = false;
        }, 3000);
      }
    }

    /**
     * Render error state
     */
    renderError() {
      this.container.innerHTML = `
        <div style="
          padding: 20px;
          text-align: center;
          background: ${this.colours.errorBg};
          border-radius: 8px;
          border: 1px solid ${this.colours.errorBorder};
        ">
          <p style="color: ${this.colours.errorText}; margin: 0;">
            Error loading replacement parts. Please try again later.
          </p>
        </div>
      `;
    }

    /**
     * Helper: Format money
     * Handles both cents (integer) and decimal string formats
     */
    formatMoney(price) {
      // If price is a string that looks like "249.00", parse it
      if (typeof price === 'string' && price.includes('.')) {
        return `£${parseFloat(price).toFixed(2)}`;
      }


      // Otherwise assume it's in cents (integer) and convert
      const amount = (price / 100).toFixed(2);
      return `£${amount}`;
    }

    /**
     * Helper: Get image URL with size
     */
    getImageUrl(image, size) {
      if (typeof image === 'string') {
        // Handle URL strings
        if (image.includes('_')) {
          return image.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${size}$2`);
        }
        return image;
      }

      // Handle image objects
      if (image && image.src) {
        if (image.src.includes('_')) {
          return image.src.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${size}$2`);
        }
        return image.src;
      }

      return '';
    }

    /**
     * Helper: Escape HTML
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * SVG Icons
     */
    getCheckShieldSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
      `;
    }

    getArrowIconSVG() {
      return `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
      `;
    }

    getLayersIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      `;
    }

    getCloseIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      `;
    }

    /**
     * Destroy widget
     */
    destroy() {
      this.container.innerHTML = '';
    }
  }

  // Register widget
  window.ProductWidgets.widgets.PartsFinderWidget = PartsFinderWidget;
})();
